import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { OnboardingScreenCreateRequest, OnboardingScreenEditRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { isDefined } from "@/helpers/util";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { onboardingScreenMutations } from "@/queries/onboardingScreens";
import { useOnboardingScreenQueries } from "@/queries/onboardingScreens/queryOptions";
import { useProjectQueries } from "@/queries/project";
import { routes } from "@/routes";

import type { LayoutProps } from "./Layout";

export function Loader(props: { children: (props: LayoutProps) => React.ReactNode }): React.ReactNode {
  const slug = useSlug();
  const { id: onboardingScreenId } = useParams<{ id: string }>();
  const isEditMode = isDefined(onboardingScreenId);
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();

  const projectQueries = useProjectQueries();
  const onboardingScreenQueries = useOnboardingScreenQueries();
  const {
    data: onboardingScreenDetails,
    isLoading: isLoadingOnboardingScreenDetails,
    error: onboardingScreenDetailsError,
  } = useQuery({
    ...onboardingScreenQueries.details({ onboardingScreenId: onboardingScreenId! }),
    enabled: isEditMode,
  });
  const {
    data: projectDetails,
    isPending: isLoadingProjectDetails,
    error: projectDetailsError,
  } = useQuery(projectQueries.details());
  const addOnboardingScreenMutation = onboardingScreenMutations.useAddOnboardingScreen();
  const updateOnboardingScreenMutation = onboardingScreenMutations.useUpdateOnboardingScreen();

  const error = onboardingScreenDetailsError || projectDetailsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingOnboardingScreenDetails || isLoadingProjectDetails;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  return props.children({
    styling: {
      bgColor: projectDetails?.styling.navigationColor,
      textColor: projectDetails?.styling.textLightColor,
    },
    isEditMode,
    defaultValues: isEditMode
      ? {
          title: onboardingScreenDetails!.title,
          subtitle: onboardingScreenDetails!.subtitle,
          image: [onboardingScreenDetails!.image],
        }
      : {
          title: "",
          subtitle: "",
          image: [],
        },
    isSubmitting: isUploadingImage || updateOnboardingScreenMutation.isPending || addOnboardingScreenMutation.isPending,
    async onSubmit(data) {
      if (data.image.length === 0) {
        throw new Error("Image not provided");
      }

      const image = await uploadFormImage(data.image[0]);

      if (!image) return;

      const payload: OnboardingScreenEditRequest | OnboardingScreenCreateRequest = {
        title: data.title,
        subtitle: data.subtitle,
        imageId: image.id,
      };

      if (isEditMode) {
        await updateOnboardingScreenMutation.mutateAsync({
          onboardingScreenId,
          data: payload,
        });
      } else {
        await addOnboardingScreenMutation.mutateAsync(payload);
      }

      void navigate(routes.onboardingScreens.list({ slug }));
    },
  });
}
